// Drift Team Website 2025
// src/api/auth.js
// Created by Lukas Helebrandt on 17.03.2025.
// Updated by Claude on 19.04.2025 to fix token handling for API response structure

import { post, get } from '../utils/api';
import { TOKEN_STORAGE_KEY, AUTH_STORAGE_KEY, REFRESH_TOKEN_STORAGE_KEY } from '../config';

// Login function
export const login = async (email, password) => {
  try {
    const response = await post('/auth/login', { email, password });
    console.log('[auth] Full login response:', response);
    
    // Struktura odpovědi z API: { token, user }
    if (response && response.token) {
      // Uložit access token
      localStorage.setItem(TOKEN_STORAGE_KEY, response.token);
      
      // Pro refresh token použijeme stejný token (záložní řešení)
      localStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, response.token);
      
      // Uložit user data
      if (response.user) {
        localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(response.user));
      }
      
      // Vrátíme strukturu, kterou očekává Login.jsx
      return {
        accessToken: response.token,
        refreshToken: response.token, // Používáme stejný token jako refresh token
        user: response.user || response
      };
    } else {
      console.error('[auth] Invalid login response structure:', response);
      throw new Error('Invalid login response: Missing token');
    }
  } catch (error) {
    console.error('[auth] Login error:', error);
    throw error;
  }
};

// Logout function
export const logout = async () => {
  try {
    await post('/auth/logout', {});
  } catch (error) {
    console.error('Logout error:', error);
  } finally {
    // Clear local storage regardless of server response
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
    localStorage.removeItem(AUTH_STORAGE_KEY);
  }
};

// Check if user is authenticated
export const checkAuth = async () => {
  const token = localStorage.getItem(TOKEN_STORAGE_KEY);
  
  if (!token) {
    return null;
  }
  
  try {
    const userData = await get('/auth/me');
    return userData;
  } catch (error) {
    // If verification fails, clear storage
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
    localStorage.removeItem(AUTH_STORAGE_KEY);
    return null;
  }
};

// Refresh token function
export const refreshToken = async () => {
  const refreshToken = localStorage.getItem(REFRESH_TOKEN_STORAGE_KEY);
  if (!refreshToken) {
    throw new Error('No refresh token available');
  }

  try {
    const data = await post('/auth/refresh', { token: refreshToken });
    if (data.token) {
      localStorage.setItem(TOKEN_STORAGE_KEY, data.token);
      localStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, data.token);
      return {
        accessToken: data.token,
        refreshToken: data.token
      };
    }
    throw new Error('Invalid refresh token response');
  } catch (error) {
    // Clear storage if refresh fails
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
    localStorage.removeItem(AUTH_STORAGE_KEY);
    throw error;
  }
};

// Register new user (admin only)
export const registerUser = async (userData) => {
  return await post('/auth/register', userData);
};

// Update user profile
export const updateProfile = async (userData) => {
  const response = await post('/auth/profile', userData);
  
  // Update local storage
  if (response) {
    localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(response));
  }
  
  return response;
};

// Change password
export const changePassword = async (currentPassword, newPassword) => {
  return await post('/auth/change-password', {
    currentPassword,
    newPassword
  });
};

// Request password reset
export const requestPasswordReset = async (email) => {
  return await post('/auth/forgot-password', { email });
};

// Reset password with token
export const resetPassword = async (token, newPassword) => {
  return await post('/auth/reset-password', {
    token,
    newPassword
  });
};